/**
 * Widget translation map — lightweight i18n without external dependencies.
 * Keys are dot-delimited identifiers; values are display strings.
 * Supports {placeholder} interpolation via the useTranslation hook.
 */

export type Locale = 'en' | 'vi';

const en = {
  'error.loadWidget': 'Unable to load chat widget.',
  'error.configNotFound': 'Configuration not found',
  'error.tryAgain': 'Try again',
  'connection.lost': 'Connection lost.',
  'connection.reconnecting': 'Reconnecting...',
  'connection.tryAgain': 'Try again',
  'chat.offline': 'Chat is offline',
  'chat.poweredBy': 'Powered by',
  'chat.sendMessage': 'Send message',
  'chat.attachFile': 'Attach file',
  'system.escalated': 'You have been connected to a human agent.',
  'system.agentJoined': '{name} has joined the conversation.',
  'system.conversationEnded': 'This conversation has ended.',
  'file.tooLarge': 'File too large. Maximum size is {maxMb}MB.',
  'file.typeNotAllowed': 'File type not allowed.',
  'offline.emailPlaceholder': 'Your email address',
  'offline.notifyMe': 'Notify me when you\'re back',
  'offline.confirmation': 'We\'ll notify you when we\'re back online.',
};

const vi: Record<TranslationKey, string> = {
  'error.loadWidget': 'Không thể tải tiện ích chat.',
  'error.configNotFound': 'Không tìm thấy cấu hình',
  'error.tryAgain': 'Thử lại',
  'connection.lost': 'Mất kết nối.',
  'connection.reconnecting': 'Đang kết nối lại...',
  'connection.tryAgain': 'Thử lại',
  'chat.offline': 'Chat đang ngoại tuyến',
  'chat.poweredBy': 'Được hỗ trợ bởi',
  'chat.sendMessage': 'Gửi tin nhắn',
  'chat.attachFile': 'Đính kèm tệp',
  'system.escalated': 'Bạn đã được kết nối với nhân viên hỗ trợ.',
  'system.agentJoined': '{name} đã tham gia cuộc trò chuyện.',
  'system.conversationEnded': 'Cuộc trò chuyện đã kết thúc.',
  'file.tooLarge': 'Tệp quá lớn. Kích thước tối đa là {maxMb}MB.',
  'file.typeNotAllowed': 'Loại tệp không được phép.',
  'offline.emailPlaceholder': 'Địa chỉ email của bạn',
  'offline.notifyMe': 'Thông báo khi trực tuyến trở lại',
  'offline.confirmation': 'Chúng tôi sẽ thông báo khi trực tuyến trở lại.',
} as const;

/** Translation keys are auto-derived from the `en` object — adding a key there enforces it in all locales. */
export type TranslationKey = keyof typeof en;

export const translations: Record<Locale, Record<TranslationKey, string>> = { en, vi };
